import React, { useState, useMemo, useEffect } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  BarChart3,
  Users,
  Smartphone,
  Gift,
  Star,
  CreditCard,
  Settings,
  Link2,
  LogOut,
  ChevronDown,
  User,
  UserCheck,
  ChefHat,
  Martini,
  ShoppingCart,
  Grid3x3,
  PanelLeftClose,
  PanelLeftOpen,
  Menu,
  Shield,
  ArrowLeftRight,
  Crown,
  Briefcase,
  CalendarDays,
  Megaphone,
  Package,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguageStore } from '../store/useLanguageStore';
import { useIsMobile } from '../hooks/useIsMobile';
import { SYSTEM_ROLES } from '../services/teamService';
import { db } from '../firebase/config';
import { doc, getDoc } from 'firebase/firestore';
import ExpressOrderModal from './ExpressOrderModal';
import NotificationDropdown from './NotificationDropdown';
import BranchSwitcher from './BranchSwitcher';
import ThemeToggle from './ui/ThemeToggle';
import { useCriticalAnomaliesCount } from '../hooks/useCriticalAnomaliesCount';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from './ui/sheet';

// Colores de rol: el texto usa patrón base(claro)+dark:(oscuro actual) para legibilidad
// en claro sin alterar el oscuro. Fondos/bordes translúcidos sirven en ambos temas.
const ROLE_STYLES = {
  superAdmin: { icon: Shield, color: 'text-amber-700 dark:text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/20', glow: 'shadow-amber-500/10' },
  owner:      { icon: Crown, color: 'text-yellow-700 dark:text-yellow-400', bg: 'bg-yellow-500/10', border: 'border-yellow-500/20', glow: 'shadow-yellow-500/10' },
  admin:      { icon: Shield, color: 'text-purple-700 dark:text-purple-400', bg: 'bg-purple-500/10', border: 'border-purple-500/20', glow: 'shadow-purple-500/10' },
  cajero:     { icon: CreditCard, color: 'text-kumia-green', bg: 'bg-kumia-green/10', border: 'border-kumia-green/20', glow: 'shadow-green-500/10' },
  mesero:     { icon: UserCheck, color: 'text-blue-700 dark:text-blue-400', bg: 'bg-blue-500/10', border: 'border-blue-500/20', glow: 'shadow-blue-500/10' },
  cocina:     { icon: ChefHat, color: 'text-orange-700 dark:text-orange-400', bg: 'bg-orange-500/10', border: 'border-orange-500/20', glow: 'shadow-orange-500/10' },
  default:    { icon: Briefcase, color: 'text-content-secondary', bg: 'bg-gray-500/10', border: 'border-line-active/20', glow: 'shadow-gray-500/10' },
};

const DashboardLayout = ({ children }) => {
  const location = useLocation();
  const { restaurantId: urlRestaurantId } = useParams();
  const { currentUser: user, restaurantProfile: restaurant, logout, restaurantId, customClaims, isOwner, isSuperAdmin, hasOrg, switchRestaurant, verifyRestaurantAccess } = useAuth();
  const navigate = useNavigate();

  // Sucursales: si la URL apunta a una sede distinta a la del contexto (deep-link
  // o refresh parado en una secundaria), sincronizar el contexto a la sede de la URL.
  // Evita mostrar datos de otra sede. Solo para miembros de cadena.
  useEffect(() => {
    if (hasOrg && urlRestaurantId && urlRestaurantId !== restaurantId && verifyRestaurantAccess(urlRestaurantId)) {
      switchRestaurant(urlRestaurantId);
    }
  }, [hasOrg, urlRestaurantId, restaurantId, verifyRestaurantAccess, switchRestaurant]);
  const { t } = useLanguageStore();
  const { isDesktop } = useIsMobile();
  const [showAppsSubmenu, setShowAppsSubmenu] = useState(false);
  const [showExpressOrderModal, setShowExpressOrderModal] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const basePath = restaurantId ? `/${restaurantId}` : '/';

  const userPermissions = customClaims?.staffData?.permissions ||
    customClaims?.permissions ||
    {};

  const hasPermission = (permissionKey) => {
    if (isOwner || isSuperAdmin) return true;
    return userPermissions[permissionKey] === true;
  };

  // ¿Puede ver TODAS las solicitudes de garzón? Solo admin/owner/superAdmin.
  // El resto del staff (garzón/cajero/host) ve únicamente las de sus mesas
  // asignadas. Debe coincidir con la regla de Firestore (isRestaurantStaff).
  const canViewAllRequests = useMemo(() => {
    if (isOwner || isSuperAdmin) return true;
    const roles = Array.isArray(customClaims?.roles)
      ? customClaims.roles
      : (customClaims?.role ? [customClaims.role] : []);
    return roles
      .filter((r) => typeof r === 'string')
      .map((r) => r.toLowerCase())
      .some((r) => r === 'admin' || r === 'admin_rest');
  }, [isOwner, isSuperAdmin, customClaims]);

  const [customRoleName, setCustomRoleName] = useState(null);

  // RoleId exacto del usuario (sin modificar) para rutas Firestore; clave en minúsculas solo para lookup de estilos
  const { currentRoleIdRaw, currentRoleKey } = useMemo(() => {
    if (isSuperAdmin) return { currentRoleIdRaw: 'superAdmin', currentRoleKey: 'superadmin' };
    if (isOwner) return { currentRoleIdRaw: 'owner', currentRoleKey: 'owner' };

    const roles = Array.isArray(customClaims?.roles)
      ? customClaims.roles
      : (customClaims?.role ? [customClaims.role] : []);

    const raw = roles.find(r => r && typeof r === 'string' && r.toLowerCase() !== 'customer') || null;
    const key = raw ? raw.toLowerCase() : null;
    return { currentRoleIdRaw: raw, currentRoleKey: key };
  }, [customClaims, isSuperAdmin, isOwner]);

  const currentRoleId = currentRoleKey;

  // Si el rol no es del sistema, intentar leer el nombre desde claims (staffData.roleName).
  // Solo hacer getDoc como fallback para retrocompatibilidad con claims que aún no tienen roleName.
  useEffect(() => {
    if (!currentRoleIdRaw) {
      setCustomRoleName(null);
      return;
    }
    if (currentRoleKey === 'superadmin' || SYSTEM_ROLES[currentRoleKey]) {
      setCustomRoleName(null);
      return;
    }
    if (!restaurantId) {
      setCustomRoleName(null);
      return;
    }

    // Primero: intentar leer roleName de claims (evita getDoc)
    const claimRoleName = customClaims?.staffData?.roleName;
    if (claimRoleName) {
      setCustomRoleName(claimRoleName);
      return;
    }

    // Fallback: leer de Firestore (usuarios con claims antiguos sin roleName)
    let cancelled = false;
    const fetchRoleName = async () => {
      try {
        const roleRef = doc(db, 'restaurants', restaurantId, 'roles', currentRoleIdRaw);
        const roleDoc = await getDoc(roleRef);

        if (cancelled) return;

        if (roleDoc.exists()) {
          setCustomRoleName(roleDoc.data().name || currentRoleIdRaw);
        } else {
          setCustomRoleName(null);
        }
      } catch (err) {
        console.warn('[DashboardLayout] Error al obtener nombre del rol:', err?.message || err);
        setCustomRoleName(null);
      }
    };

    fetchRoleName();
    return () => { cancelled = true; };
  }, [currentRoleIdRaw, currentRoleKey, restaurantId, customClaims]);

  const roleInfo = useMemo(() => {
    const styleKey = currentRoleId === 'superadmin' ? 'superAdmin' : currentRoleId;
    const style = ROLE_STYLES[styleKey] || ROLE_STYLES.default;
    const systemRole = SYSTEM_ROLES[currentRoleId];
    let label;
    if (currentRoleId === 'superadmin') {
      label = 'Super Admin';
    } else if (systemRole) {
      label = systemRole.name;
    } else {
      label = customRoleName || 'Colaborador';
    }
    return { ...style, label };
  }, [currentRoleId, customRoleName]);

  // Anomalías críticas activas del módulo Inventario — solo subscribe si el módulo
  // está habilitado para evitar reads innecesarios. Alimenta el badge rojo del sidebar.
  const inventoryEnabled = restaurant?.enabledModules?.inventory === true;
  const criticalAnomalyCount = useCriticalAnomaliesCount(restaurantId, inventoryEnabled);

  const allNavigation = [
    { name: t('dashboard'), href: basePath, icon: BarChart3, exact: true, permissionKey: 'dashboard' },
    { name: t('clients'), href: `${basePath}/clients`, icon: Users, permissionKey: 'clients' },
    { name: 'Gestión de Mesas', href: `${basePath}/table-management`, icon: Grid3x3, permissionKey: 'tableManagement' },
    { name: 'Gestión de Cuentas', href: `${basePath}/account-management`, icon: CreditCard, permissionKey: 'accountManagement' },
    {
      name: t('apps'),
      icon: Smartphone,
      permissionKey: 'apps',
      submenu: [
        { name: t('consumerExperience'), href: `${basePath}/customer-experience`, icon: User, permissionKey: 'customerExperience' },
        { name: t('staffAmplifier'), href: `${basePath}/staff-amplifier`, icon: UserCheck, permissionKey: 'staffAmplifier' },
        { name: t('kitchenMatrix'), href: `${basePath}/kitchen-matrix`, icon: ChefHat, permissionKey: 'kitchenMatrix' },
        { name: t('mixologyLab'), href: `${basePath}/mixology-lab`, icon: Martini, permissionKey: 'mixologyLab' },
      ]
    },
    { name: t('rewards'), href: `${basePath}/rewards`, icon: Gift, permissionKey: 'rewards' },
    { name: 'Marketing', href: `${basePath}/marketing`, icon: Megaphone, permissionKey: 'marketing' },
    { name: t('reviews'), href: `${basePath}/reviews`, icon: Star, permissionKey: 'reviews' },
    { name: 'Reservas', href: `${basePath}/reservations`, icon: CalendarDays, permissionKey: 'reservations' },
    // Módulo Inventario: gated por feature flag enabledModules.inventory (super admin toggle).
    // Si el flag no está activo se omite del sidebar aunque el usuario tenga el permiso.
    // El badge se rellena con anomalías críticas activas — ver criticalAnomalyCount abajo.
    ...(restaurant?.enabledModules?.inventory === true
      ? [{
          name: 'Inventario',
          href: `${basePath}/inventory`,
          icon: Package,
          permissionKey: 'inventoryManagement',
          badge: criticalAnomalyCount > 0 ? criticalAnomalyCount : null,
        }]
      : []),
    { name: t('billing'), href: `${basePath}/billing`, icon: CreditCard, permissionKey: 'billing' },
    { name: t('restaurantConfig'), href: `${basePath}/restaurant-config`, icon: Settings, permissionKey: 'settings' },
    { name: t('collaborators'), href: `${basePath}/collaborators`, icon: UserCheck, permissionKey: 'collaborators' },
    { name: t('integrations'), href: `${basePath}/integrations`, icon: Link2, permissionKey: 'integrations' },
  ];

  const navigation = allNavigation
    .map(item => {
      if (item.submenu) {
        const filteredSubmenu = item.submenu.filter(subItem => hasPermission(subItem.permissionKey));
        if (filteredSubmenu.length === 0) return null;
        return { ...item, submenu: filteredSubmenu };
      }
      return hasPermission(item.permissionKey) ? item : null;
    })
    .filter(Boolean);

  const handleLogout = () => {
    logout();
  };

  const handleNavClick = () => {
    if (!isDesktop) {
      setIsMobileMenuOpen(false);
    }
  };

  const renderNavigation = () => (
    <nav className="sidebar-nav">
      {navigation.map((item) => {
        if (item.submenu) {
          return (
            <div key={item.name}>
              <button
                onClick={() => setShowAppsSubmenu(!showAppsSubmenu)}
                className={`sidebar-nav-item w-full justify-between ${showAppsSubmenu ? 'active' : ''}`}
              >
                <div className="flex items-center gap-3">
                  <item.icon size={20} />
                  {item.name}
                </div>
                <ChevronDown
                  size={16}
                  className={`transform transition-transform ${showAppsSubmenu ? 'rotate-180' : ''}`}
                />
              </button>
              {showAppsSubmenu && (
                <div className="ml-4">
                  {item.submenu.map((subItem) => (
                    <Link
                      key={subItem.name}
                      to={subItem.href}
                      onClick={handleNavClick}
                      className={`sidebar-nav-item ${location.pathname === subItem.href ? 'active' : ''}`}
                    >
                      <subItem.icon size={18} />
                      {subItem.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          );
        }

        const isActive = item.exact
          ? location.pathname === item.href
          : location.pathname === item.href;

        return (
          <Link
            key={item.name}
            to={item.href}
            onClick={handleNavClick}
            className={`sidebar-nav-item ${isActive ? 'active' : ''} relative`}
          >
            <item.icon size={20} />
            <span className="flex-1">{item.name}</span>
            {item.badge != null && item.badge > 0 && (
              <span
                className="ml-auto inline-flex items-center justify-center min-w-[20px] h-[20px] px-1.5 rounded-full bg-red-500 text-content-primary text-[11px] font-bold leading-none"
                aria-label={`${item.badge} anomalías críticas`}
                title={`${item.badge} anomalía${item.badge !== 1 ? 's' : ''} crítica${item.badge !== 1 ? 's' : ''} activa${item.badge !== 1 ? 's' : ''}`}
              >
                {item.badge > 99 ? '99+' : item.badge}
              </span>
            )}
          </Link>
        );
      })}
    </nav>
  );

  return (
    <div className="flex h-screen bg-surface-app">
      {/* Desktop Sidebar */}
      {isDesktop && (
        <div
          className="sidebar transition-all duration-300 ease-in-out hidden lg:block"
          style={{
            width: isSidebarCollapsed ? 0 : '280px',
            minWidth: isSidebarCollapsed ? 0 : '280px',
            overflow: isSidebarCollapsed ? 'hidden' : 'auto',
            opacity: isSidebarCollapsed ? 0 : 1
          }}
        >
          <div className="sidebar-logo">
            <img
              src={`${process.env.PUBLIC_URL || ''}/assets/kumia-logo.webp`}
              alt="KumIA"
            />
            <div>
              <div className="sidebar-brand-name">
                {restaurant?.name || 'Mi Restaurante KumIA'}
              </div>
              <div className="sidebar-brand-subtitle">
                Powered by KumIA Technology
              </div>
            </div>
          </div>
          <div className="mx-3 mb-2">
            <div className={`flex items-center gap-2 px-3 py-2 ${roleInfo.bg} border ${roleInfo.border} rounded-lg shadow-sm ${roleInfo.glow}`}>
              <roleInfo.icon size={14} className={`${roleInfo.color} flex-shrink-0`} />
              <span className={`text-xs font-semibold ${roleInfo.color}`}>{roleInfo.label}</span>
            </div>
            {isSuperAdmin && (
              <button
                onClick={() => navigate('/admin/select-restaurant')}
                className="flex items-center gap-2 w-full mt-2 px-3 py-2 text-sm text-content-muted hover:text-content-primary hover:bg-surface-3 rounded-lg transition-colors"
              >
                <ArrowLeftRight size={14} />
                Cambiar Restaurante
              </button>
            )}
            {hasOrg && (
              <button
                onClick={() => navigate('/chain')}
                className="flex items-center gap-2 w-full mt-2 px-3 py-2 text-sm text-content-muted hover:text-content-primary hover:bg-surface-3 rounded-lg transition-colors"
              >
                <ArrowLeftRight size={14} />
                Mis sedes
              </button>
            )}
          </div>
          {renderNavigation()}
        </div>
      )}

      {/* Mobile/Tablet Sidebar Sheet */}
      {!isDesktop && (
        <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen} modal={false}>
          <SheetContent side="left" hideClose onOverlayClick={() => setIsMobileMenuOpen(false)} onInteractOutside={(e) => e.preventDefault()} onPointerDownOutside={(e) => e.preventDefault()} className="w-[280px] !p-0 !gap-0 !top-[56px] !h-[calc(100vh-56px)] bg-surface-chrome border-r border-line-subtle flex flex-col">
            <SheetHeader className="p-0">
              <SheetTitle className="sr-only">Menu de navegación</SheetTitle>
            </SheetHeader>

            {/* Logo - fijo arriba */}
            <div className="flex items-center gap-3 px-4 py-3 border-b border-line-subtle flex-shrink-0">
              <img
                src={`${process.env.PUBLIC_URL || ''}/assets/kumia-logo.webp`}
                alt="KumIA"
                className="w-8 h-8"
                style={{ filter: 'drop-shadow(0 0 10px rgba(122, 201, 67, 0.5))' }}
              />
              <div>
                <div className="text-base font-bold text-content-primary leading-tight">
                  {restaurant?.name || 'Mi Restaurante KumIA'}
                </div>
                <div className="text-[0.7rem] text-content-muted mt-0.5">
                  Powered by KumIA Technology
                </div>
              </div>
            </div>

            {/* Role badge - mobile */}
            <div className="px-4 py-2 border-b border-line-subtle flex-shrink-0">
              <div className={`flex items-center gap-2 px-3 py-1.5 ${roleInfo.bg} border ${roleInfo.border} rounded-lg shadow-sm ${roleInfo.glow}`}>
                <roleInfo.icon size={14} className={`${roleInfo.color} flex-shrink-0`} />
                <span className={`text-xs font-semibold ${roleInfo.color}`}>{roleInfo.label}</span>
              </div>
              {isSuperAdmin && (
                <button
                  onClick={() => { setIsMobileMenuOpen(false); navigate('/admin/select-restaurant'); }}
                  className="flex items-center gap-2 w-full mt-1.5 px-3 py-1.5 text-sm text-content-muted hover:text-content-primary hover:bg-surface-3 rounded-lg transition-colors"
                >
                  <ArrowLeftRight size={14} />
                  Cambiar Restaurante
                </button>
              )}
              {hasOrg && (
                <button
                  onClick={() => { setIsMobileMenuOpen(false); navigate('/chain'); }}
                  className="flex items-center gap-2 w-full mt-1.5 px-3 py-1.5 text-sm text-content-muted hover:text-content-primary hover:bg-surface-3 rounded-lg transition-colors"
                >
                  <ArrowLeftRight size={14} />
                  Mis sedes
                </button>
              )}
            </div>

            {/* Navegación - scrollable */}
            <div className="flex-1 overflow-y-auto min-h-0">
              {renderNavigation()}
            </div>

            {/* User info - fijo abajo */}
            <div className="flex-shrink-0 px-4 py-3 border-t border-line-subtle">
              <div className="flex items-center gap-2 text-content-secondary text-sm mb-3">
                <User size={16} className="text-kumia-green" />
                <span className="truncate">{user?.email || user?.displayName || 'Usuario'}</span>
              </div>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 w-full bg-red-600/20 hover:bg-red-600/30 text-red-700 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 px-3 py-2 rounded-lg font-medium text-sm transition-all duration-200 border border-red-600/30"
              >
                <LogOut size={16} />
                {t('logout')}
              </button>
            </div>
          </SheetContent>
        </Sheet>
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top Navbar */}
        <header className="top-navbar">
          <div className="navbar-restaurant-info flex items-center gap-2 md:gap-3">
            {/* Mobile/Tablet: hamburger menu button */}
            {!isDesktop ? (
              <button
                onClick={() => setIsMobileMenuOpen((prev) => !prev)}
                className="relative z-[101] flex items-center justify-center w-10 h-10 bg-surface-3/50 hover:bg-surface-4/50 text-content-secondary hover:text-content-primary rounded-lg transition-all duration-200 border border-line-strong/50"
                title={isMobileMenuOpen ? 'Cerrar menú' : 'Abrir menú'}
              >
                <Menu size={20} />
              </button>
            ) : (
              <button
                onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                className="flex items-center justify-center w-10 h-10 bg-surface-3/50 hover:bg-surface-4/50 text-content-secondary hover:text-content-primary rounded-lg transition-all duration-200 border border-line-strong/50 hover:border-line-active/50"
                title={isSidebarCollapsed ? 'Mostrar sidebar' : 'Ocultar sidebar'}
              >
                {isSidebarCollapsed ? (
                  <PanelLeftOpen size={20} />
                ) : (
                  <PanelLeftClose size={20} />
                )}
              </button>
            )}
            <button
              onClick={() => setShowExpressOrderModal(true)}
              className="flex items-center gap-2 bg-kumia-green hover:bg-kumia-green-dark text-black px-3 md:px-4 py-2 rounded-lg font-semibold text-sm transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105"
            >
              <ShoppingCart size={16} />
              <span className="hidden sm:inline">Generar Orden Express</span>
            </button>

            {/* Selector de sede (solo para miembros de una cadena) */}
            <BranchSwitcher />
          </div>

          <div className="navbar-actions">
            {/* Theme toggle (oscuro / claro) */}
            <ThemeToggle />

            {/* Notifications */}
            <NotificationDropdown
              restaurantId={restaurantId}
              staffName={user?.email || user?.displayName || 'Staff'}
              viewerUid={user?.uid}
              canViewAll={canViewAllRequests}
              canManageArrivals={hasPermission('staffAmplifier')}
            />

            {/* User Info - desktop only */}
            {isDesktop && (
              <div className="flex items-center gap-2 bg-surface-3/50 backdrop-blur-sm rounded-lg px-3 py-2 border border-line-strong/50">
                <User size={16} className="text-kumia-green" />
                <span className="text-sm text-content-secondary">
                  {user?.email || user?.displayName || 'Usuario'}
                </span>
              </div>
            )}

            {/* Logout - desktop only */}
            {isDesktop && (
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 bg-red-600/20 hover:bg-red-600/30 text-red-700 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 px-3 py-2 rounded-lg font-medium text-sm transition-all duration-200 border border-red-600/30 hover:border-red-500/50"
                title={t('logout')}
              >
                <LogOut size={16} />
                <span className="hidden sm:inline">{t('logout')}</span>
              </button>
            )}
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-auto bg-surface-app relative">
          {children}
        </main>
      </div>

      {/* Modal Generar Orden Express */}
      <ExpressOrderModal
        isOpen={showExpressOrderModal}
        onClose={() => setShowExpressOrderModal(false)}
      />
    </div>
  );
};

export default DashboardLayout;
